import { useCallback, useMemo, useState, type MouseEvent } from 'react';
import { CopyGenerationWidgetKey } from '@inference.engine/widgets/web';
import { ChevronDownIcon, ChevronUpIcon, CopyIcon, Info } from 'lucide-react';
import { Button, Separator } from '@monksflow/monks-ui';
import {
  getWidgetStores,
  InfoMenu,
  InfoMenuType,
  type InfoMenuInput,
  type WidgetFullModeProps,
} from '@/entities/widget';
import { notificationService } from '@/lib/services/notification.service';
import { logger } from '@/lib/utils/logger';
import { SidePanel } from '@/shared/ui/SidePanelWrapper';
import { useSaveToProjectAction } from '@/shared/ui';
import {
  buildCopySectionText,
  formatDateTimeAbsolute,
  humanizeChannel,
  humanizeResponseStyle,
} from '../lib/copy-widget.utils';
import type {
  CopyEditableField,
  CopyGenerationItem,
  CopyGenerationWidgetData,
  CopyGenerationWidgetProps,
  CopyVariationSection,
} from '../model/copy-widget.types';
import { DownloadFormatDropdown, type ExportFormat } from './components/DownloadFormatDropdown';
import { EditableText } from './components/EditableText/EditableText';
import { ChannelTab, CopyCard, CopyCardBody, CopyCardTitle } from './CopyWidget.parts';
import { useTranslation } from 'react-i18next';

// dependency injection — `useArtifactExport` is injected via the REQUIRED `exportDeps`
// prop. Production wiring lives in the config-layer factory at
// `@/lib/config/agents.config` (which pre-binds `getFileDownloadUrl` from
// `@/features/chats`). Tests must supply a no-op stub explicitly.
//
// `exportDeps` is REQUIRED (no
// optional `?? DEFAULT_EXPORT_DEPS` fallback). The previous optional+
// fallback pattern toggled hook identity if the parent flipped
// `props.exportDeps` between undefined and defined across renders —
// real `useArtifactExport` consumes 3+ React hook slots, the noop
// consumes 0, so the toggle violates the Rules of Hooks ("Rendered
// more hooks than during the previous render"). Forcing every
// consumer to provide a stable deps bag eliminates the toggle path.
const transformCopyData = (raw: unknown): CopyGenerationWidgetData => {
  try {
    if (typeof raw === 'string') {
      return JSON.parse(raw) as CopyGenerationWidgetData;
    }
    if (raw && typeof raw === 'object') {
      return raw as CopyGenerationWidgetData;
    }
  } catch {
    // ignore and fall through to empty
  }
  return { items: [] };
};

export const CopyWidget = (props: WidgetFullModeProps<CopyGenerationWidgetProps>) => {
  const { t } = useTranslation('contentTest');

  const raw = props.data as unknown;

  // In the Library preview mount (`readOnly`) the inline copy editors render as
  // static, non-editable text. Read affordances (copy, download, info, channel
  // tabs, collapse) stay enabled — none of them mutate or navigate away.
  const isReadOnly = props.readOnly ?? false;

  // Store + chats access via the widget binding seam.
  // See `entities/widget/lib/widget-store-bindings`. Production wiring at
  // app boot supplies the real `@/app/store/widget` + `@/features/chats`
  // modules; tests stub `getWidgetStores()` at the seam.
  const { widgetStore, chatStore } = getWidgetStores();
  const { useChatStore, ChatMarkdownRenderer } = chatStore;

  // dependency injection — `useArtifactExport` injected by the config-layer factory
  // wrapping this widget (see `@/lib/config/agents.config`). The wrapper
  // pre-binds `getFileDownloadUrl` from `@/features/chats` so the widget
  // itself never imports it. `exportDeps` is REQUIRED — the prop type guarantees a stable deps bag; no
  // hook-identity toggle path exists.
  const useArtifactExport = props.exportDeps.useArtifactExport;

  // (Rule 1 derived-inline): recompute when `raw` identity changes
  // instead of syncing via `useEffect(setState)`. `lastRaw` is the identity we
  // last derived from; when `raw` changes we replace `data` in-place during
  // render.
  const [data, setData] = useState<CopyGenerationWidgetData>(() => transformCopyData(raw));
  const [lastRaw, setLastRaw] = useState<unknown>(raw);
  if (raw !== lastRaw) {
    setLastRaw(raw);
    setData(transformCopyData(raw));
  }

  // Always work with a safe items array
  const items = useMemo<CopyGenerationItem[]>(
    () => (Array.isArray(data.items) ? data.items : []),
    [data.items],
  );

  // Channels (first-seen order)
  const channels = useMemo(() => {
    const list: string[] = [];
    const seen = new Set<string>();
    for (const item of items) {
      const channel = item.channel.trim();
      if (channel && !seen.has(channel)) {
        seen.add(channel);
        list.push(channel);
      }
    }
    return list;
  }, [items]);

  const showAllChip = channels.length > 1;

  /* eslint-disable i18next/no-literal-string -- channel filter sentinel value */
  const [selectedChannel, setSelectedChannel] = useState<string>(
    showAllChip ? 'all' : channels.at(0) || 'all',
  );

  // (derived-inline): reconcile the selected channel when the
  // `channels` list changes rather than chaining an effect. `channelsKey` is
  // the identity we last reconciled against.
  const [lastChannelsKey, setLastChannelsKey] = useState<string>(() => channels.join('\u0000'));
  const channelsKey = channels.join('\u0000');
  if (channelsKey !== lastChannelsKey) {
    setLastChannelsKey(channelsKey);
    if (channels.length === 0) {
      if (selectedChannel !== 'all') setSelectedChannel('all');
    } else if (!showAllChip && selectedChannel === 'all') {
      setSelectedChannel(channels[0]);
    } else if (selectedChannel !== 'all' && !channels.includes(selectedChannel)) {
      setSelectedChannel(showAllChip ? 'all' : channels[0]);
    }
  }
  /* eslint-enable i18next/no-literal-string */

  const [infoMenuAnchor, setInfoMenuAnchor] = useState<HTMLElement | null>(null);

  // Collapse state (per variation section)
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set());

  // Version selector support. The bound widget slice is loose-typed at the
  // seam (`{ agentName: string; [key: string]: unknown }`); copy-widget
  // narrows the slice it actually reads here.
  const { widget: rawWidget } = widgetStore.useWidgetPanel();
  const widget = rawWidget as {
    agentName: string;
    artifactId?: string;
    exchangeId?: string;
  } | null;
  const agentId = widget?.agentName ?? '';
  const versionConfig = widgetStore.useWidgetVersions(agentId);
  const createArtifactVersion = widgetStore.useCreateArtifactVersion() as {
    mutate: (
      args: {
        chatId: string;
        exchangeId: string;
        key: string;
        name: string;
        kind: string;
        data: CopyGenerationWidgetData;
      },
      options: { onSuccess: () => void; onError: (err: unknown) => void },
    ) => void;
  };

  const artifactId = widget?.artifactId ?? '';

  // "Save to project" — the shared hook handles gating (flag + workspace + live
  // source artifact) and returns null when the action shouldn't render.
  // eslint-disable-next-line i18next/no-literal-string -- artifact kind
  const saveAction = useSaveToProjectAction('copy', artifactId);

  // Create separate export hooks for each format
  // Use templateName as referenceKey to cache each format separately.
  // The injected `useArtifactExport` (dependency injection) pre-binds `getFileDownloadUrl`
  // Widget passes only the format-specific options.
  /* eslint-disable i18next/no-literal-string -- export format identifiers and download file name stem */
  const mdExport = useArtifactExport(artifactId, 'md', {
    fileName: data.title || 'copy',
    templateName: 'md',
  });

  const docxExport = useArtifactExport(artifactId, 'docx', {
    fileName: data.title || 'copy',
    templateName: 'docx',
  });

  const xlsxExport = useArtifactExport(artifactId, 'xlsx', {
    fileName: data.title || 'copy',
    templateName: 'xlsx',
  });
  /* eslint-enable i18next/no-literal-string */

  // Combined loading state for UI
  const isDownloading =
    mdExport.isDownloading || docxExport.isDownloading || xlsxExport.isDownloading;

  const handleInfoMenuOpen = (event: MouseEvent<HTMLButtonElement>) => {
    setInfoMenuAnchor(event.currentTarget);
  };

  const handleInfoMenuClose = () => {
    setInfoMenuAnchor(null);
  };

  const title = useMemo(
    () =>
      data.title ??
      (selectedChannel === 'all' ? t('copyWidget.all') : humanizeChannel(selectedChannel)),
    [data.title, selectedChannel, t],
  );

  // Filter by selected channel
  const filteredItems = useMemo<CopyGenerationItem[]>(
    () => (selectedChannel === 'all' ? items : items.filter((i) => i.channel === selectedChannel)),
    [items, selectedChannel],
  );

  // Group items by channel to compute 1-based variation numbers per channel
  const sections = useMemo<CopyVariationSection[]>(() => {
    const byChannel = new Map<string, CopyGenerationItem[]>();
    for (const it of filteredItems) {
      if (!byChannel.has(it.channel)) byChannel.set(it.channel, []);
      byChannel.get(it.channel)!.push(it);
    }

    const out: CopyVariationSection[] = [];
    for (const [channel, list] of byChannel.entries()) {
      list.forEach((item, idx) => {
        const variation = idx + 1;
        out.push({
          id: `${channel}#${variation}`,
          channel,
          variation,
          item,
          headerLabel: t('copyWidget.sectionHeader', {
            channel: humanizeChannel(channel),
            variation,
          }),
        });
      });
    }

    // Preserve original order: by first appearance of channel, then order within that channel
    const channelOrder = channels;
    out.sort((a, b) => {
      const ca = channelOrder.indexOf(a.channel);
      const cb = channelOrder.indexOf(b.channel);
      if (ca !== cb) return ca - cb;
      return a.variation - b.variation;
    });

    return out;
  }, [filteredItems, channels, t]);

  const toggleSection = useCallback((sectionId: string) => {
    setCollapsedSections((prev) => {
      const next = new Set(prev);
      if (next.has(sectionId)) next.delete(sectionId);
      else next.add(sectionId);
      return next;
    });
  }, []);

  // Save handler used by all EditableText instances
  const handleSaveField = useCallback(
    (sectionId: string, field: CopyEditableField, newValue: string) => {
      const section = sections.find((s) => s.id === sectionId);
      if (!section) return;

      const { item } = section;
      const itemIndex = data.items.findIndex((it) => it === item);
      if (itemIndex === -1) return;

      const newItems = data.items.map((it, idx) => {
        if (idx !== itemIndex) return it;

        if (field === 'hashtags') {
          const hashtags = newValue
            .split(/\s+/)
            .map((t) => t.trim())
            .filter(Boolean);
          return {
            ...it,
            hashtags,
          };
        }

        return {
          ...it,
          [field]: newValue,
        } as CopyGenerationItem;
      });

      const updatedData: CopyGenerationWidgetData = {
        ...data,
        items: newItems,
        createdAt: new Date().toISOString(),
      };

      // Optimistic local update
      setData(updatedData);

      // The bound chat-store state is loose-typed at the seam; copy-widget
      // narrows `chatId` to a string here for the artifact-version mutation.
      const chatId = useChatStore.getState().chatId as string | undefined;
      const exchangeId = widget?.exchangeId;

      if (!chatId || !exchangeId) {
        logger.warn('Missing chatId or exchangeId, skipping artifact version creation');
        return;
      }

      createArtifactVersion.mutate(
        {
          chatId,
          exchangeId,
          key: CopyGenerationWidgetKey,
          // eslint-disable-next-line i18next/no-literal-string -- persisted artifact name that `apps/api` writes with the same literal; getArtifactDisplayName translates it on read
          name: 'Copy Generation Widget',
          // eslint-disable-next-line i18next/no-literal-string -- artifact kind
          kind: 'copy',
          data: updatedData,
        },
        {
          onSuccess: () => {
            notificationService.showSuccess(t('copyWidget.copyUpdated'));
          },
          onError: (err) => {
            logger.error('Failed to create copy widget artifact version', err);
            notificationService.showError(t('copyWidget.failedToSaveEditedCopy'));
          },
        },
      );
    },
    [sections, data, widget, createArtifactVersion, useChatStore, t],
  );

  // Per-section copy
  const handleCopySection = useCallback(
    (header: string, sectionItem: CopyGenerationItem) => {
      const text = buildCopySectionText(header, [sectionItem]);
      if (!text.trim()) return;

      navigator.clipboard
        .writeText(text)
        .then(() => {
          notificationService.showSuccess(t('copyWidget.copiedToClipboard', { header }), {
            preventDuplicate: true,
          });
        })
        .catch((err: unknown) => {
          logger.error('Copy section failed:', err);
          notificationService.showError(t('copyWidget.couldNotCopyToClipboard'));
        });
    },
    [t],
  );

  const handleDownload = useCallback(
    (format: ExportFormat) => {
      if (!artifactId) {
        notificationService.showError(t('copyWidget.unableToDownloadArtifactNot'));
        return;
      }

      const exportMap: Record<ExportFormat, typeof mdExport> = {
        md: mdExport,
        docx: docxExport,
        xlsx: xlsxExport,
      };

      void exportMap[format].download();
    },
    [artifactId, mdExport, docxExport, xlsxExport, t],
  );

  // Info popover data
  const infoSettings = useMemo<InfoMenuInput>(() => {
    const rows: InfoMenuInput = [];
    const text = (value: string) => ({ type: InfoMenuType.Text, value });

    // Date
    const formattedDate = formatDateTimeAbsolute(data.createdAt);
    if (formattedDate) rows.push({ [t('copyWidget.infoMenu.date')]: text(formattedDate) });

    // Model
    const model =
      // eslint-disable-next-line i18next/no-literal-string -- model identifier fallback, compared not rendered
      (data.settings as unknown as { model?: string } | undefined)?.model?.toString() ?? 'GPT-5';
    rows.push({ [t('copyWidget.infoMenu.model')]: text(model) });

    // Channels (all available)
    const channelLabel = channels.length ? channels.map(humanizeChannel).join(', ') : '—';
    rows.push({ [t('copyWidget.infoMenu.channel')]: text(channelLabel) });

    // Response Style
    rows.push({
      [t('copyWidget.infoMenu.responseStyle')]: text(
        humanizeResponseStyle(data.settings?.responseStyle),
      ),
    });

    // Goal
    const goal =
      data.settings?.goals && data.settings.goals.trim().length > 0
        ? data.settings.goals
        : t('copyWidget.infoMenu.na');
    rows.push({ [t('copyWidget.infoMenu.goal')]: text(goal) });

    // QA
    if (data.settings?.enableQAFeedback) {
      rows.push({ [t('copyWidget.infoMenu.qa')]: text(t('copyWidget.infoMenu.enabled')) });
    }

    // Prompt
    if (data.settings?.prompt) {
      rows.push({
        [t('copyWidget.infoMenu.prompt')]: {
          type: InfoMenuType.LongText,
          value: data.settings.prompt,
        },
      });
    }

    return rows;
  }, [data.createdAt, data.settings, channels, t]);

  return (
    <SidePanel
      title={title}
      headerConfig={{
        chip: {
          label: t('copyWidget.copy'),
          color: 'green',
        },
        versionConfig,
        actions: [
          {
            type: 'icon',
            icon: <Info size={20} />,
            onClick: handleInfoMenuOpen,
            tooltip: t('copyWidget.viewPromptInfo'),
          },
          {
            type: 'custom',
            component: (
              <DownloadFormatDropdown onDownload={handleDownload} isDownloading={isDownloading} />
            ),
          },
          ...(saveAction ? [saveAction] : []),
        ],
      }}
    >
      <div className="flex-1 overflow-y-auto mx-8 mt-2 mb-4">
        {channels.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-1.5">
            {showAllChip && (
              <ChannelTab
                label={t('copyWidget.all')}
                // eslint-disable-next-line i18next/no-literal-string -- channel filter sentinel value
                onClick={() => setSelectedChannel('all')}
                selected={selectedChannel === 'all'}
              />
            )}
            {channels.map((ch) => (
              <ChannelTab
                key={ch}
                label={humanizeChannel(ch)}
                onClick={() => setSelectedChannel(ch)}
                selected={selectedChannel === ch}
              />
            ))}
          </div>
        )}

        <div className="mt-2 pr-2">
          {sections.length === 0 ? (
            <p className="text-body-lg text-muted-foreground">{t('copyWidget.noCopyFound')}</p>
          ) : (
            <div className="space-y-8">
              {sections.map((section) => {
                const { id, headerLabel, item, channel, variation } = section;
                const isCollapsed = collapsedSections.has(id);

                const bodyLabel =
                  channel === 'instagram'
                    ? t('copyWidget.copyWidgetUtils.caption')
                    : channel === 'plain'
                      ? t('copyWidget.copyWidgetUtils.text')
                      : t('copyWidget.copyWidgetUtils.body');
                const bodyValue = channel === 'plain' ? item.text : item.body;
                const bodyIsMarkdown = channel === 'plain';

                const hashtagsDisplay =
                  Array.isArray(item.hashtags) && item.hashtags.length > 0
                    ? item.hashtags.map((tag) => (tag.startsWith('#') ? tag : `#${tag}`)).join(' ')
                    : '';

                return (
                  <div key={id} className="space-y-4">
                    <Separator className="mb-1.25" />
                    <div className="flex items-center">
                      <h3 className="text-heading-md font-semibold">
                        {humanizeChannel(channel)}{' '}
                        <span className="font-normal">
                          {t('copyWidget.copyVariation', { variation })}
                        </span>
                      </h3>
                      <div className="ml-auto inline-flex gap-1">
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          onClick={() => handleCopySection(headerLabel, item)}
                          aria-label={t('copyWidget.copySection')}
                        >
                          <CopyIcon className="size-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          onClick={() => toggleSection(id)}
                          aria-label={
                            isCollapsed
                              ? t('copyWidget.expandSection')
                              : t('copyWidget.collapseSection')
                          }
                        >
                          {isCollapsed ? <ChevronDownIcon /> : <ChevronUpIcon />}
                        </Button>
                      </div>
                    </div>
                    {!isCollapsed && (
                      <div className="grid gap-4">
                        {channel === 'email' ? (
                          <>
                            <CopyCard>
                              <CopyCardTitle>{t('copyWidget.subjectLine')}</CopyCardTitle>
                              <EditableText
                                value={item.subject ?? ''}
                                disabled={isReadOnly}
                                onSave={(next) => handleSaveField(id, 'subject', next)}
                                renderDisplay={(val) => <CopyCardBody>{val}</CopyCardBody>}
                              />
                            </CopyCard>
                            <CopyCard>
                              <CopyCardTitle>{t('copyWidget.preheader')}</CopyCardTitle>
                              <EditableText
                                value={item.preheader ?? ''}
                                disabled={isReadOnly}
                                onSave={(next) => handleSaveField(id, 'preheader', next)}
                                renderDisplay={(val) => <CopyCardBody>{val}</CopyCardBody>}
                              />
                            </CopyCard>
                            <CopyCard>
                              <CopyCardTitle>{t('copyWidget.heroImageCopy')}</CopyCardTitle>
                              <EditableText
                                value={item.heroImageCopy ?? ''}
                                disabled={isReadOnly}
                                onSave={(next) => handleSaveField(id, 'heroImageCopy', next)}
                                renderDisplay={(val) => <CopyCardBody>{val}</CopyCardBody>}
                              />
                            </CopyCard>
                            <CopyCard>
                              <CopyCardTitle>{t('copyWidget.bodyTitle')}</CopyCardTitle>
                              <EditableText
                                value={item.bodyTitle ?? ''}
                                disabled={isReadOnly}
                                onSave={(next) => handleSaveField(id, 'bodyTitle', next)}
                                renderDisplay={(val) => <CopyCardBody>{val}</CopyCardBody>}
                              />
                            </CopyCard>
                            <CopyCard>
                              <CopyCardTitle>{t('copyWidget.bodyCopy')}</CopyCardTitle>
                              <EditableText
                                value={item.bodyCopy ?? ''}
                                multiline
                                disabled={isReadOnly}
                                onSave={(next) => handleSaveField(id, 'bodyCopy', next)}
                                renderDisplay={(val) => <CopyCardBody>{val}</CopyCardBody>}
                              />
                            </CopyCard>
                            <CopyCard>
                              <CopyCardTitle>{t('copyWidget.bodyImageCopy')}</CopyCardTitle>
                              <EditableText
                                value={item.bodyImageCopy ?? ''}
                                disabled={isReadOnly}
                                onSave={(next) => handleSaveField(id, 'bodyImageCopy', next)}
                                renderDisplay={(val) => <CopyCardBody>{val}</CopyCardBody>}
                              />
                            </CopyCard>
                            <CopyCard>
                              <CopyCardTitle>{t('copyWidget.primaryCta')}</CopyCardTitle>
                              <EditableText
                                value={item.primaryCta ?? ''}
                                disabled={isReadOnly}
                                onSave={(next) => handleSaveField(id, 'primaryCta', next)}
                                renderDisplay={(val) => <CopyCardBody>{val}</CopyCardBody>}
                              />
                            </CopyCard>
                            <CopyCard>
                              <CopyCardTitle>{t('copyWidget.secondaryCta')}</CopyCardTitle>
                              <EditableText
                                value={item.secondaryCta ?? ''}
                                disabled={isReadOnly}
                                onSave={(next) => handleSaveField(id, 'secondaryCta', next)}
                                renderDisplay={(val) => <CopyCardBody>{val}</CopyCardBody>}
                              />
                            </CopyCard>
                            <CopyCard>
                              <CopyCardTitle>{t('copyWidget.footer')}</CopyCardTitle>
                              <EditableText
                                value={item.footer ?? ''}
                                multiline
                                disabled={isReadOnly}
                                onSave={(next) => handleSaveField(id, 'footer', next)}
                                renderDisplay={(val) => <CopyCardBody>{val}</CopyCardBody>}
                              />
                            </CopyCard>
                          </>
                        ) : (
                          <>
                            {channel === 'facebook' && !!item.headline && (
                              <CopyCard>
                                <CopyCardTitle>{t('copyWidget.headline')}</CopyCardTitle>
                                <EditableText
                                  value={item.headline ?? ''}
                                  disabled={isReadOnly}
                                  onSave={(next) => handleSaveField(id, 'headline', next)}
                                  renderDisplay={(val) => <CopyCardBody>{val}</CopyCardBody>}
                                />
                              </CopyCard>
                            )}
                            <CopyCard>
                              <CopyCardTitle>{bodyLabel}</CopyCardTitle>
                              <EditableText
                                value={bodyValue ?? ''}
                                multiline
                                disabled={isReadOnly}
                                onSave={(next) =>
                                  handleSaveField(id, channel === 'plain' ? 'text' : 'body', next)
                                }
                                renderDisplay={(val) =>
                                  bodyIsMarkdown && val ? (
                                    <div className="text-foreground">
                                      <ChatMarkdownRenderer content={val} />
                                    </div>
                                  ) : (
                                    <CopyCardBody>{val}</CopyCardBody>
                                  )
                                }
                              />
                            </CopyCard>
                            {channel === 'facebook' && !!item.linkDescription && (
                              <CopyCard>
                                <CopyCardTitle>{t('copyWidget.linkDescription')}</CopyCardTitle>
                                <EditableText
                                  value={item.linkDescription ?? ''}
                                  disabled={isReadOnly}
                                  onSave={(next) => handleSaveField(id, 'linkDescription', next)}
                                  renderDisplay={(val) => <CopyCardBody>{val}</CopyCardBody>}
                                />
                              </CopyCard>
                            )}
                            {channel === 'instagram' &&
                              Array.isArray(item.hashtags) &&
                              item.hashtags.length > 0 && (
                                <CopyCard>
                                  <CopyCardTitle>{t('copyWidget.hashtags')}</CopyCardTitle>
                                  <EditableText
                                    value={hashtagsDisplay}
                                    disabled={isReadOnly}
                                    onSave={(next) => handleSaveField(id, 'hashtags', next)}
                                    renderDisplay={(val) => <CopyCardBody>{val}</CopyCardBody>}
                                  />
                                </CopyCard>
                              )}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
      <InfoMenu
        input={infoSettings}
        anchorEl={infoMenuAnchor}
        open={Boolean(infoMenuAnchor)}
        onClose={handleInfoMenuClose}
      />
    </SidePanel>
  );
};
